import React from 'react'
import { IconInfoCircle } from '@tabler/icons-react'

import { Button } from '@/components/buttons/Button'
import { SegmentedControl } from '@/components/buttons/SegmentedControl'

import { LOGIC_COMBINATOR, LOGIC_COMBINATOR_COPY, LOGIC_MISSING_INPUT_NOTE } from '../helpers'

/**
 * How a logic field combines its conditions: the literal `all` / `any` its
 * description stores, picked rather than typed, with what the chosen one does
 * spelled out beneath it. Shared by the Add Field modal and the field's row.
 *
 * @param {object} props
 * @param {string} props.value The stored combinator, 'all' or 'any'
 * @param {Function} props.onChange Called with the newly picked combinator
 * @param {boolean} [props.disabled] Locks both choices (read-only or inherited rows)
 * @param {boolean} [props.small] Compact control, for the field list
 * @param {string} props.testIdPrefix Prefix for the segment and explanation test ids
 */
export const LogicCombinator = ({ value, onChange, disabled = false, small, testIdPrefix }) => {
  const explanation = LOGIC_COMBINATOR_COPY[value]?.explanation

  return (
    <div className="rtn-logic-combinator__control">
      <SegmentedControl
        small={small}
        items={Object.values(LOGIC_COMBINATOR).map((combinator) => ({
          accessor: combinator,
          label: LOGIC_COMBINATOR_COPY[combinator].label,
          active: value === combinator,
          disabled,
          onClick: () => onChange(combinator),
          tooltip: LOGIC_COMBINATOR_COPY[combinator].explanation,
          dataTestId: `${testIdPrefix}-${combinator}`,
        }))}
      />
      {explanation && (
        <span
          className="rtn-logic-combinator__explanation"
          data-testid={`${testIdPrefix}-explanation`}
        >
          {explanation}
          {/* A button so the note is reachable by keyboard too: the tooltip
              opens on focus as well as hover. */}
          <Button
            basic
            xs
            square
            type="button"
            className="rtn-logic-combinator__info"
            aria-label="How a field with no answer counts"
            tooltip={LOGIC_MISSING_INPUT_NOTE}
            tooltipProps={{ placement: 'top' }}
            icon={<IconInfoCircle />}
            data-testid={`${testIdPrefix}-missing-input-info`}
          />
        </span>
      )}
    </div>
  )
}
